import type { ReactNode } from 'react'
import type { Bootstrap } from './api/types'
import { createContext, use, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router'
import { api } from './api/client'
import { PERIOD_RE, shiftMonth } from './period'

/**
 * PRD-016 §7.18 step-up modal body: "a box stating exactly what is being authorized (action and object, for example
 * 'Approve and post · Journal entry · Inventory true-up · $2,140.00 · Northwind Retail LLC')" and "a confirm button
 * that names the action ('Confirm and post')". A plain string is the one-line form older callers pass (UX audit
 * 2026-09-19 P1-4 turned the Review card's into this shape). Stripe's confirm dialogs read the same way: the verb,
 * the object, the amount, the account.
 */
export interface StepUpDetail {
  /** The verb as the person reads it: "Approve and post", "Reverse and post", "Archive". */
  action: string
  /** The object: "Journal entry · Adjustment · Sep 2026". */
  object: string
  amount?: string | undefined
  company?: string | undefined
  memo?: string | undefined
  /** Extra lines the box shows under the object (a period, a note). */
  lines?: string[] | undefined
  /** The confirm button's words; defaults to "Confirm". */
  confirm?: string | undefined
}
export type StepUpLabel = string | StepUpDetail

/** Shell state that follows the person from page to page (PRD-018 §3.3, §3.9): period, tree, density, palette, step-up. */
export interface StepUpRequest { tier: 'tier1' | 'tier2', action: string, label: StepUpLabel, resolve: (ok: boolean) => void }

export interface ShellState {
  bootstrap: Bootstrap
  period: string
  setPeriod: (p: string) => void
  /** Whether `[` / `]` can move: false at the ends of the 18-back / 3-ahead range (audit P2-10). */
  canShiftPeriod: (by: -1 | 1) => boolean
  treeCollapsed: boolean
  toggleTree: () => void
  collapsedClusters: ReadonlySet<string>
  toggleCluster: (id: string) => void
  paletteOpen: boolean
  setPaletteOpen: (open: boolean, initialQuery?: string) => void
  paletteInitial: string
  sheetOpen: boolean
  setSheetOpen: (open: boolean) => void
  /** PRD-020 §3.1 "+ New client": one dialog, opened from the tree, the palette or Home; the frame renders it. */
  newClientOpen: boolean
  setNewClientOpen: (open: boolean) => void
  stepUp: StepUpRequest | null
  requestStepUp: (tier: 'tier1' | 'tier2', action: string, label: StepUpLabel) => Promise<boolean>
  resolveStepUp: (ok: boolean) => void
  preview: boolean
}

const ShellContext = createContext<ShellState | null>(null)
export function useShell(): ShellState {
  const v = use(ShellContext)
  if (!v)
    throw new Error('useShell outside ShellProvider')
  return v
}

const PREVIEW_ALLOWED = import.meta.env.VITE_ALLOW_PREVIEW === '1'
/** PRD-018 §3.12: below this width the tree starts as the icon rail so the 1180 px grid fits. */
const WIDE_QUERY = '(min-width: 1440px)'
const isWide = () => typeof window === 'undefined' || window.matchMedia(WIDE_QUERY).matches

/** True while any modal layer is up: the drawer, a confirm, the palette, the step-up, the sheet. */
function overlayOpen(): boolean {
  return typeof document !== 'undefined' && document.querySelector('[role="dialog"], [role="alertdialog"], [cmdk-dialog]') !== null
}

export function ShellProvider({ bootstrap, children }: { bootstrap: Bootstrap, children: ReactNode }) {
  const [params, setParams] = useSearchParams()
  const navigate = useNavigate()
  const urlPeriod = params.get('period')
  const [periodState, setPeriodState] = useState<string>(() => (urlPeriod && PERIOD_RE.test(urlPeriod) ? urlPeriod : bootstrap.period.default))
  const period = periodState
  // The stored preference wins on a wide screen; between 1024 and 1439 the rail is the default (§3.12).
  const [treeCollapsed, setTreeCollapsed] = useState(() => bootstrap.me.settings.treeCollapsed || !isWide())
  const [collapsedClusters, setCollapsedClusters] = useState<Set<string>>(() => new Set(bootstrap.me.settings.collapsedClusters))
  const [paletteOpen, setPaletteOpen_] = useState(false)
  const [paletteInitial, setPaletteInitial] = useState('')
  const [sheetOpen, setSheetOpen] = useState(false)
  const [newClientOpen, setNewClientOpen] = useState(false)
  const [stepUp, setStepUp] = useState<StepUpRequest | null>(null)
  const saveTimerRef = useRef<number | null>(null)

  // Deep link with ?period wins over the stored choice (PRD-018 §3.13).
  useEffect(() => {
    if (urlPeriod && PERIOD_RE.test(urlPeriod) && urlPeriod !== period)
      setPeriodState(urlPeriod) // eslint-disable-line react/set-state-in-effect -- URL is the source of truth for the period; syncing it is the effect's job
  }, [urlPeriod]) // eslint-disable-line react/exhaustive-deps

  // ADR 0032: the firm's open month changed while this shell was up (a CFO set or held it, or Keep/Hold on the banner
  // refetched the bootstrap). Follow the new default unless a deep link pins the period; the person's own picks
  // afterwards stand as usual. Runs only when the open month itself moves, never on other bootstrap refetches.
  const currentOpen = bootstrap.period.currentOpen
  const defaultPeriod = bootstrap.period.default
  useEffect(() => {
    if (!(urlPeriod && PERIOD_RE.test(urlPeriod)))
      setPeriodState(defaultPeriod) // eslint-disable-line react/set-state-in-effect -- the server's open month is the source of truth for the default
  }, [currentOpen]) // eslint-disable-line react/exhaustive-deps

  // Crossing 1440 px: collapse to the rail below it, restore the stored preference above it.
  useEffect(() => {
    const mq = window.matchMedia(WIDE_QUERY)
    const onChange = (e: MediaQueryListEvent) => setTreeCollapsed(e.matches ? bootstrap.me.settings.treeCollapsed : true)
    mq.addEventListener('change', onChange)
    return () => mq.removeEventListener('change', onChange)
  }, [bootstrap.me.settings.treeCollapsed])

  const persist = useCallback((patch: Record<string, unknown>) => {
    if (saveTimerRef.current)
      window.clearTimeout(saveTimerRef.current)
    saveTimerRef.current = window.setTimeout(() => {
      api('/me/settings', { method: 'PUT', json: patch }).catch(() => undefined)
    }, 400)
  }, [])

  const setPeriod = useCallback((p: string) => {
    const clamped = p < bootstrap.period.range.from ? bootstrap.period.range.from : p > bootstrap.period.range.to ? bootstrap.period.range.to : p
    setPeriodState(clamped)
    persist({ lastPeriod: clamped })
    if (params.has('period')) {
      const next = new URLSearchParams(params)
      next.set('period', clamped)
      setParams(next, { replace: false })
    }
  }, [bootstrap.period.range, params, persist, setParams])

  const canShiftPeriod = useCallback((by: -1 | 1) => by < 0 ? period > bootstrap.period.range.from : period < bootstrap.period.range.to, [period, bootstrap.period.range])

  const toggleTree = useCallback(() => {
    setTreeCollapsed((v) => {
      persist({ treeCollapsed: !v })
      return !v
    })
  }, [persist])

  const toggleCluster = useCallback((id: string) => {
    setCollapsedClusters((prev) => {
      const next = new Set(prev)
      if (next.has(id))
        next.delete(id)
      else next.add(id)
      persist({ collapsedClusters: [...next] })
      return next
    })
  }, [persist])

  const setPaletteOpen = useCallback((open: boolean, initialQuery = '') => {
    setPaletteInitial(initialQuery)
    setPaletteOpen_(open)
  }, [])

  const requestStepUp = useCallback((tier: 'tier1' | 'tier2', action: string, label: StepUpLabel) => new Promise<boolean>((resolve) => {
    setStepUp({ tier, action, label, resolve })
  }), [])
  const resolveStepUp = useCallback((ok: boolean) => {
    setStepUp((cur) => {
      cur?.resolve(ok)
      return null
    })
  }, [])

  const value = useMemo<ShellState>(() => ({
    bootstrap,
    period,
    setPeriod,
    canShiftPeriod,
    treeCollapsed,
    toggleTree,
    collapsedClusters,
    toggleCluster,
    paletteOpen,
    setPaletteOpen,
    paletteInitial,
    sheetOpen,
    setSheetOpen,
    newClientOpen,
    setNewClientOpen,
    stepUp,
    requestStepUp,
    resolveStepUp,
    preview: PREVIEW_ALLOWED && params.get('preview') === 'r1',
  }), [bootstrap, period, setPeriod, canShiftPeriod, treeCollapsed, toggleTree, collapsedClusters, toggleCluster, paletteOpen, setPaletteOpen, paletteInitial, sheetOpen, newClientOpen, stepUp, requestStepUp, resolveStepUp, params])

  // `[` and `]` are handled here so they exist on every route (PRD-018 §3.3). Suppressed in fields and
  // under any dialog, so a keypress in the drawer never moves the checklist behind it (audit P2-10).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const el = e.target as HTMLElement | null
      if (el && (['INPUT', 'TEXTAREA', 'SELECT'].includes(el.tagName) || el.isContentEditable))
        return
      if (e.metaKey || e.ctrlKey || e.altKey)
        return
      if (e.key !== '[' && e.key !== ']')
        return
      if (paletteOpen || sheetOpen || newClientOpen || stepUp || overlayOpen())
        return
      e.preventDefault()
      const by = e.key === '[' ? -1 : 1
      if (canShiftPeriod(by))
        setPeriod(shiftMonth(period, by))
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [period, setPeriod, canShiftPeriod, navigate, paletteOpen, sheetOpen, newClientOpen, stepUp])

  return <ShellContext value={value}>{children}</ShellContext>
}
